import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Loader2, Send } from "lucide-react";

import { toast } from "sonner";
import { fetchProducts, submitInquiry } from "../lib/api";
import { INQUIRY } from "../constants/testIds/site";

const empty = {
  name: "",
  company: "",
  email: "",
  phone: "",
  location: "",
  product_name: "",
  product_slug: "",
  quantity: "",
  budget: "",
  expected_delivery: "",
  requirements: "",
};

export default function InquiryDialog({
  open,
  onOpenChange,
  product = null,
}) {
  const [form, setForm] = useState(empty);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadProducts() {
      try {
        const data = await fetchProducts({ limit: 200 });
        if (!cancelled) {
          setProducts(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) {
          setProducts([]);
        }
      }
    }

    loadProducts();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (product) {
      setForm((f) => ({
        ...f,
        product_name: product.name,
        product_slug: product.slug,
      }));
    }
  }, [product]);

  const upd = (key) => (e) => {
    setForm((f) => ({
      ...f,
      [key]: e.target.value,
    }));
  };

  const onProductChange = (e) => {
    const selectedSlug = e.target.value;
    if (!selectedSlug) {
      setForm((f) => ({ ...f, product_slug: "", product_name: "" }));
      return;
    }

    const selectedProduct = products.find((item) => item.slug === selectedSlug);
    setForm((f) => ({
      ...f,
      product_slug: selectedSlug,
      product_name: selectedProduct?.name || "",
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.name ||
      !form.email ||
      !form.phone ||
      !form.requirements
    ) {
      toast.error(
        "Please fill in name, email, phone and requirements."
      );
      return;
    }

    setLoading(true);

    try {
      await submitInquiry(form);

      toast.success(
        "Enquiry received. Our team will reach out within one business day."
      );

      setForm(empty);
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Unable to submit right now. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={INQUIRY.dialog}
        className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="overline mb-2 text-accent">
            Request Quote
          </div>

          <DialogTitle className="font-display text-2xl font-bold tracking-tight text-brand-navy">
            {product
              ? `Enquire about ${product.name}`
              : "Tell us about your project"}
          </DialogTitle>

          <DialogDescription className="text-slate-500">
            Share your requirements — our engineering team will respond within
            one business day.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={onSubmit}
          className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2"
        >
          <Field label="Full name *">
            <Input
              value={form.name}
              onChange={upd("name")}
              data-testid={INQUIRY.name}
            />
          </Field>

          <Field label="Company">
            <Input
              value={form.company}
              onChange={upd("company")}
              data-testid={INQUIRY.company}
            />
          </Field>

          <Field label="Email *">
            <Input
              type="email"
              value={form.email}
              onChange={upd("email")}
              data-testid={INQUIRY.email}
            />
          </Field>

          <Field label="Phone *">
            <Input
              value={form.phone}
              onChange={upd("phone")}
              data-testid={INQUIRY.phone}
            />
          </Field>

          <Field label="Location">
            <Input
              value={form.location}
              onChange={upd("location")}
              data-testid={INQUIRY.location}
            />
          </Field>

          <Field label="Product Interested">
            <select
              value={form.product_slug || ""}
              onChange={onProductChange}
              data-testid={INQUIRY.product}
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm outline-none"
            >
              <option value="">Select a product</option>
              {products.map((item) => (
                <option key={item.slug} value={item.slug}>
                  {item.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Quantity">
            <Input
              value={form.quantity}
              onChange={upd("quantity")}
              data-testid={INQUIRY.quantity}
            />
          </Field>

          <Field label="Budget (Approx)">
            <Input
              value={form.budget}
              onChange={upd("budget")}
              data-testid={INQUIRY.budget}
            />
          </Field>

          <Field
            label="Expected Delivery"
            className="md:col-span-2"
          >
            <Input
              value={form.expected_delivery}
              onChange={upd("expected_delivery")}
              data-testid={INQUIRY.delivery}
            />
          </Field>

          <Field
            label="Requirements *"
            className="md:col-span-2"
          >
            <Textarea
              rows={4}
              value={form.requirements}
              onChange={upd("requirements")}
              data-testid={INQUIRY.requirements}
              placeholder="Describe your application, throughput target, integration needs, etc."
            />
          </Field>

          <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid={INQUIRY.cancel}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={loading}
              data-testid={INQUIRY.submit}
              className="gap-2 bg-accent text-white hover:bg-accent2"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}

              {loading ? "Submitting..." : "Submit Enquiry"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
  className = "",
}) {
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </Label>
      {children}
    </div>
  );
}